/**
 * AnchorPicker.ts — converts a screen-space click into the
 * mooring anchor the user tapped (bollard or buoy), or null.
 *
 * Implementation: ray-sphere intersection in world space.
 *  1. Build a world-space ray from camera-eye through the
 *     pixel under the cursor (NDC → unproject via `invViewProj`).
 *  2. Test the ray against every registered anchor approximated
 *     as a sphere (`PICK_RADIUS_M`, slightly inflated for easier
 *     tapping on touch screens).
 *  3. Return the closest hit (smallest ray-parameter `t`).
 *
 * The picker holds a snapshot of the same anchor map used by
 * `MooringPickupService` so the orchestrator only needs to wire
 * one source of truth.
 */
import type { MooringAnchor } from "./Dock";
import type { Camera } from "../Camera";
import type { AnchorKind } from "./MooringPickupService";

/**
 * Effective pick radius — bollards are ~0.4 m thick, buoys ~0.4 m
 * across; we inflate to 0.7 m so an imprecise tap (especially on
 * mobile / chase-cam where the dock reads small) still lands.
 */
const PICK_RADIUS_M = 0.7;

interface PickEntry {
  anchor: MooringAnchor;
  kind: AnchorKind;
}

/** Result of {@link AnchorPicker.pickAtNdc}. */
export interface AnchorPick {
  anchorId: string;
  kind: AnchorKind;
  /** Distance along the ray from the camera, metres. */
  distance: number;
  /** World position the user effectively tapped on. */
  worldPos: readonly [number, number, number];
}

export class AnchorPicker {
  private readonly entries = new Map<string, PickEntry>();

  constructor(private readonly camera: Camera) {}

  registerAnchor(anchor: MooringAnchor, kind: AnchorKind): void {
    this.entries.set(anchor.anchorId, { anchor, kind });
  }

  /**
   * Pick the closest anchor under the given normalised device
   * coordinates (NDC — both axes in [-1, +1], +Y up).
   *
   * Returns null if no anchor lies within `PICK_RADIUS_M` of the
   * eye-through-pixel ray.
   */
  pickAtNdc(ndcX: number, ndcY: number): AnchorPick | null {
    const m = this.camera.invViewProj;
    // Two points on the ray: near plane (z=1 in reverse-Z NDC) and
    // far plane (z=0). Unproject both and take the difference.
    const near = unproject(m, ndcX, ndcY, 1);
    const far = unproject(m, ndcX, ndcY, 0);
    const dx = far[0] - near[0];
    const dy = far[1] - near[1];
    const dz = far[2] - near[2];
    const dLen = Math.hypot(dx, dy, dz) || 1;
    const rdx = dx / dLen;
    const rdy = dy / dLen;
    const rdz = dz / dLen;
    const ox = near[0];
    const oy = near[1];
    const oz = near[2];

    let bestT = Infinity;
    let bestId: string | null = null;
    let bestKind: AnchorKind = "buoy";
    let bestPos: [number, number, number] = [0, 0, 0];
    const r2 = PICK_RADIUS_M * PICK_RADIUS_M;

    for (const { anchor, kind } of this.entries.values()) {
      const [ax, ay, az] = anchor.getWorldPos();
      // Vector from ray origin to sphere centre.
      const ex = ax - ox;
      const ey = ay - oy;
      const ez = az - oz;
      // Project onto ray direction.
      const t = ex * rdx + ey * rdy + ez * rdz;
      if (t < 0 || t >= bestT) continue; // behind camera or further
      // Closest point on ray to sphere centre.
      const cx = ox + rdx * t;
      const cy = oy + rdy * t;
      const cz = oz + rdz * t;
      const d2 = (cx - ax) ** 2 + (cy - ay) ** 2 + (cz - az) ** 2;
      if (d2 > r2) continue;
      bestT = t;
      bestId = anchor.anchorId;
      bestKind = kind;
      bestPos = [ax, ay, az];
    }

    if (!bestId) return null;
    return {
      anchorId: bestId,
      kind: bestKind,
      distance: bestT,
      worldPos: bestPos,
    };
  }
}

/**
 * Unproject NDC (x, y, z) using `invViewProj`, returning the
 * corresponding world-space point. NDC z is reverse-Z (1 = near,
 * 0 = far).
 */
function unproject(
  invVP: Float32Array | readonly number[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  const wx = invVP[0]! * x + invVP[4]! * y + invVP[8]! * z + invVP[12]!;
  const wy = invVP[1]! * x + invVP[5]! * y + invVP[9]! * z + invVP[13]!;
  const wz = invVP[2]! * x + invVP[6]! * y + invVP[10]! * z + invVP[14]!;
  const ww = invVP[3]! * x + invVP[7]! * y + invVP[11]! * z + invVP[15]!;
  const inv = ww !== 0 ? 1 / ww : 1;
  return [wx * inv, wy * inv, wz * inv];
}
